// Shared ticket constants + helpers for the Tickets and Schedule
// features. Ported from Mission Control's src/lib/tickets.ts, trimmed
// to what the XSite Command UI needs.

export const TICKET_STATUSES = ['open', 'in_progress', 'waiting', 'closed'];
export const TICKET_PRIORITIES = ['low', 'normal', 'high'];

export const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting: 'Waiting',
  closed: 'Closed'
};

export const PRIORITY_LABELS = {
  low: 'Low',
  normal: 'Normal',
  high: 'High'
};

// Status → XSite Badge tone. open=amber (needs attention), in_progress=
// teal (active), waiting=grey, closed=grey (muted).
export const STATUS_TONE = {
  open: 'amber',
  in_progress: 'teal',
  waiting: 'grey',
  closed: 'grey'
};

// Renewal tier → tone, mirroring the schedule's urgency colours.
export const TIER_TONE = {
  'past-due': 'red',
  urgent: 'amber',
  soon: 'teal',
  upcoming: 'blue',
  current: 'grey'
};
export const TIER_LABEL = {
  'past-due': 'Past due',
  urgent: 'Urgent',
  soon: 'Soon',
  upcoming: 'Upcoming',
  current: 'Current'
};

export function isUserOnTicket(t, userId) {
  if (userId == null) return false;
  if (t.primaryAssigneeId === userId) return true;
  return (t.collaborators || []).some((c) => c.user.id === userId);
}

export function hasNoAssignees(t) {
  return t.primaryAssigneeId == null && (t.collaborators || []).length === 0;
}

// Initials from a name ("David Cook" → "DC") or email local-part.
export function initialsFor(user) {
  if (!user) return '?';
  const src = user.name || user.email || '';
  const parts = String(src).split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function userLabel(user, currentUserId) {
  if (!user) return '';
  const base = user.name || user.email;
  return user.id === currentUserId ? `${base} (you)` : base;
}

// "3m ago" / "2h ago" / "yesterday" / "May 14" relative time.
export function fmtRelative(value, now = new Date()) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = now.getTime() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

export function fmtAbsolute(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-CA');
}

// Days-until phrasing for renewal rows.
export function fmtDaysUntil(days) {
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days}d`;
}

export function moneyFmt(n) {
  const v = typeof n === 'number' ? n : parseFloat(n);
  if (!Number.isFinite(v) || v === 0) return '—';
  return `$${v.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}
